/**
 * views/ExceptionTriage.tsx — /dashboard/triage/:missionId
 * ──────────────────────────────────────────────────────────
 * Step 7: Exception triage UI for Managers, Admins, and Pilots.
 *
 * This view sits between the pilot's batch upload and AI inference.
 * It shows three buckets for the selected aisle:
 *
 *   matched          — one photo decoded cleanly to one location (green)
 *   duplicates       — 2+ photos mapping to the same location (amber)
 *   missing          — assigned locations with no photo yet (red)
 *   unassigned       — photos whose QR failed to decode (amber)
 *
 * Duplicate / Unassigned resolution
 * ────────────────────────────────────
 * Each unassigned or duplicate photo has a text input for the pilot/manager
 * to type the correct location code and click "Assign & Resolve".  The UI
 * calls PATCH /api/missions/{mission_id}/photos/{photo_id}/assign which sets
 * ProcessedPhoto.manual_location_override and re-renders the triage state.
 *
 * Auto-refresh
 * ────────────
 * When photos are still being processed (processing_count > 0) the view
 * polls every 5 seconds so newly decoded results appear automatically.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, RefreshCw,
  ImageOff, ChevronLeft, X, Ban, Copy, Check,
} from 'lucide-react';

import { useAuth }              from '@/contexts/AuthContext';
import { apiClient, ApiError }  from '@/api/client';
import { Badge }                from '@/components/ui/badge';
import { Button }               from '@/components/ui/button';
import { Progress }             from '@/components/ui/progress';
import type {
  TriagePhoto,
  TriageResponse,
  TriageDuplicateGroup,
  TriageMatchedItem,
} from '@/types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtBytes(b: number | null | undefined): string {
  if (!b) return '';
  if (b < 1024)        return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtConf(c: number | null | undefined): string {
  if (c == null) return '—';
  return `${(c * 100).toFixed(0)}%`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Generate same-parity row neighbours (odd ↔ odd, even ↔ even).
 * Warehouse racking numbers pallets in alternating odd/even pairs along each
 * aisle, so a pallet's physical neighbours always share its row parity.
 *   10-011-C (odd)  → [10-009-C, 10-013-C]
 *   10-010-C (even) → [10-008-C, 10-012-C]
 */
/** Caption for the scan-info line — names the TRUE source of the decoded
 *  location (a CODE128 beam-label sticker read must not be shown as "QR"). */
function scanSourceLabel(src?: string | null): string {
  return src === 'label' ? 'Label' : src === 'ocr' ? 'OCR' : 'QR';
}

function getAdjacentLocations(locationCode: string): string[] {
  const match = locationCode.match(/^(.+?)-(\d+)-([A-Z])$/);
  if (!match) return [];
  const [, prefix, rowStr, level] = match;
  const row    = parseInt(rowStr, 10);
  const pad    = rowStr.length;
  const parity = row % 2;          // 0 = even, 1 = odd
  const out: string[] = [];
  for (const d of [-2, 2]) {       // immediate same-parity neighbours only
    const r = row + d;
    if (r > 0 && r % 2 === parity)
      out.push(`${prefix}-${String(r).padStart(pad, '0')}-${level}`);
  }
  return out;
}

/**
 * Full-screen image lightbox.
 * Accepts the raw API URL and fetches the full-resolution image on open,
 * so triage cards can load thumbnails without pre-fetching the full file.
 */
// WS6(d): prefetch the viewer derivative on hover so the enlarge opens instantly.
const _prefetchedViewers = new Set<string>();
function prefetchViewer(imageUrl: string): void {
  const url = imageUrl.replace(/\/image$/, '/viewer');
  if (_prefetchedViewers.has(url)) return;
  _prefetchedViewers.add(url);
  fetch(url, {
    headers: { Authorization: `Bearer ${sessionStorage.getItem('infinium_access_token') ?? ''}` },
  }).catch(() => {});
}

function ZoomModal({ url, filename, onClose }: { url: string; filename: string; onClose: () => void }) {
  const [src,      setSrc]      = useState<string | null>(null);  // full resolution
  const [thumbSrc, setThumbSrc] = useState<string | null>(null);  // instant placeholder
  const [loadErr,  setLoadErr]  = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Thumbnail first — it's small and usually already cached by the card, so the
  // user sees the image immediately instead of staring at a spinner.
  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    fetch(url.replace(/\/image$/, '/thumbnail'), {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('infinium_access_token') ?? ''}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => { objectUrl = URL.createObjectURL(blob); setThumbSrc(objectUrl); })
      .catch(() => {});   // non-fatal — full-res may still arrive
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  // Full resolution in the background, with a timeout so a slow/stalled request
  // (e.g. the server busy mid scan-batch) never leaves the modal stuck — it
  // falls back to the thumbnail instead of an infinite spinner.
  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45_000);
    // WS6(d): enlarge uses the ~2560px viewer derivative (fast) — the original
    // full-res image stays untouched for barcode/QR detection.
    fetch(url.replace(/\/image$/, '/viewer'), {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('infinium_access_token') ?? ''}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => { clearTimeout(timeout); objectUrl = URL.createObjectURL(blob); setSrc(objectUrl); })
      .catch(() => { clearTimeout(timeout); setLoadErr(true); });
    return () => { clearTimeout(timeout); controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  const display = src ?? thumbSrc;   // prefer full-res, fall back to the thumbnail

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white bg-black/40 rounded-full p-1.5 z-10"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      {display ? (
        <>
          <img
            src={display}
            alt={filename}
            className={`max-w-full max-h-[92vh] object-contain rounded shadow-2xl transition-[filter] duration-300 ${src ? '' : 'blur-[2px]'}`}
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/50 text-xs font-mono pointer-events-none">
            {src
              ? `${filename} · Esc or click outside to close`
              : loadErr
              ? `${filename} · full-resolution unavailable — showing preview`
              : `${filename} · loading full resolution…`}
          </p>
        </>
      ) : loadErr ? (
        <p className="text-white/60 text-sm">Failed to load image</p>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="h-8 w-8 animate-spin text-white/40" />
          <p className="text-white/50 text-sm">Loading preview…</p>
        </div>
      )}
    </div>
  );
}

// ── Photo thumbnail card ──────────────────────────────────────────────────────

function PhotoCard({
  photo,
  missionId,
  onAssigned,
  showAssignInput = true,
  dim = false,
  takenLocations = new Set(),
}: {
  photo: TriagePhoto;
  missionId: string;
  onAssigned: () => void;
  showAssignInput?: boolean;
  dim?: boolean;
  takenLocations?: Set<string>;
}) {
  const [imgSrc,       setImgSrc]       = useState<string | null>(null);
  const [imgError,     setImgError]     = useState(false);
  const [code,         setCode]         = useState(photo.manual_location ?? '');
  const [busy,         setBusy]         = useState(false);
  const [err,          setErr]          = useState<string | null>(null);
  const [scanditResult, setScanditResult] = useState<string | null>(null);
  const [zoomed,        setZoomed]        = useState(false);

  // Fetch the thumbnail (small preview) for the card.  Full-res is only loaded
  // when the user clicks to zoom — handled inside ZoomModal.
  const thumbnailUrl = photo.image_url.replace(/\/image$/, '/thumbnail');

  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000);
    fetch(thumbnailUrl, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('infinium_access_token') ?? ''}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        clearTimeout(timeout);
        objectUrl = URL.createObjectURL(blob);
        setImgSrc(objectUrl);
        // Run Scandit on the blob URL — fires after image is in DOM.
        import('../services/scanditScanner').then(({ scanImageForQR }) =>
          scanImageForQR(objectUrl!).then(val => { if (val) setScanditResult(val); })
        );
      })
      .catch(() => { clearTimeout(timeout); setImgError(true); });
    return () => { clearTimeout(timeout); controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photo.image_url]);

  const assign = async () => {
    if (!code.trim()) return;
    const normalised = code.trim().toUpperCase();

    // Warn if another photo already holds this location (and it's not the
    // photo's own current location — that would just be a harmless re-save).
    const currentLoc = (photo.effective_location ?? '').toUpperCase();
    if (normalised !== currentLoc && takenLocations.has(normalised)) {
      const confirmed = window.confirm(
        `⚠ Location ${normalised} already has a photo assigned.\n\n` +
        `Continuing will overwrite the existing assignment — that photo will move back to Unassigned.\n\n` +
        `Do you want to overwrite?`
      );
      if (!confirmed) return;
    }

    setBusy(true); setErr(null);
    let tid: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, rej) => { tid = setTimeout(() => rej(new Error('Request timed out — check connection and try again.')), 90_000); });
    try {
      const overwrite = normalised !== currentLoc && takenLocations.has(normalised);
      await Promise.race([
        apiClient.patch(
          `/api/missions/${missionId}/photos/${photo.photo_id}/assign`,
          { location_code: normalised, overwrite },
        ),
        timeout,
      ]);
      clearTimeout(tid!);
      onAssigned();
    } catch (e) {
      clearTimeout(tid!);
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to assign.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true); setErr(null);
    try {
      await apiClient.patch(
        `/api/missions/${missionId}/photos/${photo.photo_id}/assign`,
        { location_code: null },
      );
      setCode('');
      onAssigned();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to clear.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className={`rounded-xl border bg-white overflow-hidden shadow-sm flex flex-col ${dim ? 'opacity-60' : ''}`}>
      {/* Thumbnail */}
      <div className="relative bg-gray-100 aspect-video flex items-center justify-center overflow-hidden">
        {imgError ? (
          <div className="flex flex-col items-center gap-1 text-muted-foreground/50">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs">Preview unavailable</span>
          </div>
        ) : imgSrc ? (
          <img
            src={imgSrc}
            alt={photo.filename}
            className="w-full h-full object-contain cursor-zoom-in"
            onClick={() => setZoomed(true)}
            onMouseEnter={() => prefetchViewer(photo.image_url)}
            title="Click to enlarge"
          />
        ) : (
          <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground/40" />
        )}
        {/* Status chip */}
        <span
          className={`absolute top-1.5 right-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
            photo.status === 'DONE'       ? 'bg-green-100 text-green-800' :
            photo.status === 'FAILED'     ? 'bg-red-100 text-red-800' :
            photo.status === 'PROCESSING' ? 'bg-amber-100 text-amber-800' :
                                            'bg-gray-100 text-gray-700'
          }`}
        >
          {photo.status}
        </span>
      </div>

      {/* Metadata */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <p className="text-xs font-mono text-muted-foreground truncate" title={photo.filename}>
          {photo.filename}
        </p>
        <div className="flex gap-3 text-xs text-muted-foreground">
          {photo.scanned_location && (
            <span>{scanSourceLabel(photo.scan_source)}: <code className="text-foreground font-mono">{photo.scanned_location}</code> ({fmtConf(photo.scan_confidence)})</span>
          )}
          {!photo.scanned_location && (
            <span className="text-amber-600 font-medium">⚠ QR not decoded</span>
          )}
          {photo.file_size_bytes && <span>{fmtBytes(photo.file_size_bytes)}</span>}
        </div>

        {/* Manual override */}
        {photo.manual_location && (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-green-700 font-medium">
              ✓ Assigned: <code>{photo.manual_location}</code>
            </span>
            <button
              className="text-muted-foreground hover:text-destructive underline underline-offset-2 text-xs"
              onClick={clear}
              disabled={busy}
            >
              clear
            </button>
          </div>
        )}

        {/* Scandit QR result — shown as a chip when Scandit decoded a barcode */}
        {showAssignInput && !photo.manual_location && scanditResult && (
          <div className="space-y-1">
            <p className="text-xs font-medium flex items-center gap-1">
              <span className="text-blue-600">◈</span>
              <span className="text-muted-foreground">QR decoded — click to assign:</span>
            </p>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                className={`
                  text-xs font-mono px-2 py-0.5 rounded-full border transition-colors
                  ${code === scanditResult
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'}
                `}
                onClick={() => setCode(scanditResult)}
                disabled={busy}
                title={`Re-scanned QR: ${scanditResult}`}
              >
                {scanditResult}
              </button>
            </div>
          </div>
        )}

        {/* OCR suggestions — master-validated chips shown when QR failed */}
        {showAssignInput && !photo.manual_location &&
          photo.ocr_suggestions && photo.ocr_suggestions.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium flex items-center gap-1">
              <span className="text-green-600">✓</span>
              <span className="text-muted-foreground">
                {photo.ocr_suggestions.length > 1
                  ? `${photo.ocr_suggestions.length} verified locations detected — select the correct one:`
                  : 'Verified location detected — click to assign:'}
              </span>
            </p>
            <div className="flex flex-wrap gap-1">
              {photo.ocr_suggestions.map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  className={`
                    text-xs font-mono px-2 py-0.5 rounded-full border transition-colors
                    ${code === suggestion
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'}
                  `}
                  onClick={() => setCode(suggestion)}
                  disabled={busy}
                  title={`Use OCR-detected location: ${suggestion}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Assign input */}
        {showAssignInput && !photo.manual_location && (
          <div className="flex gap-1.5 mt-auto pt-1">
            <input
              className="form-input flex-1 text-xs py-1 px-2 font-mono uppercase"
              placeholder="Location code…"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && assign()}
              disabled={busy}
            />
            <Button
              size="sm"
              className="h-7 px-2 text-xs shrink-0"
              disabled={busy || !code.trim()}
              onClick={assign}
            >
              {busy ? '…' : 'Assign'}
            </Button>
          </div>
        )}
        {err && <p className="text-xs text-destructive">{err}</p>}
      </div>
    </div>
    {zoomed && (
      <ZoomModal url={photo.image_url} filename={photo.filename} onClose={() => setZoomed(false)} />
    )}
  </>
  );
}

// ── Large unassigned triage row ───────────────────────────────────────────────

// ── Location-code validation helper ──────────────────────────────────────────
// Filters out product/serial barcodes that the Scandit client-side re-scan
// picks up but are clearly not warehouse location codes.
// • Pure-numeric strings ≥ 7 digits → product barcode (EAN-13, Code128, etc.)
// • Too long (> 20 chars) → not a location code
// • If known locations are provided, validate strictly against that set.
function isLikelyLocationCode(code: string, knownLocations: Set<string>): boolean {
  const upper = code.trim().toUpperCase();
  if (!upper) return false;
  if (upper.length > 20) return false;
  if (/^\d{7,}$/.test(upper)) return false;   // all-numeric long → product barcode
  if (knownLocations.size > 0) return knownLocations.has(upper);
  return upper.includes('-');                  // fallback: location codes have dashes
}


function UnassignedTriageRow({
  photo, index, total, missionId, onAssigned, adjacentLocations,
  takenLocations = new Set(), knownLocations = new Set(),
  isSelected = false, onSelect, codeInjection, noOcr = false,
}: {
  photo: TriagePhoto;
  index: number;
  total: number;
  missionId: string;
  onAssigned: (assignedCode?: string) => void;
  adjacentLocations?: string[];
  takenLocations?: Set<string>;
  knownLocations?: Set<string>;
  isSelected?: boolean;
  onSelect?: () => void;
  codeInjection?: { code: string; seq: number } | null;
  /** Skip auto-OCR scan on mount (use for duplicate photos that already have QR). */
  noOcr?: boolean;
}) {
  const [imgSrc,        setImgSrc]        = useState<string | null>(null);
  const [imgError,      setImgError]      = useState(false);
  const [imgErrorCode,  setImgErrorCode]  = useState<number | null>(null);
  const [zoomed,        setZoomed]        = useState(false);
  const [code,          setCode]          = useState(photo.manual_location ?? '');
  const [busy,          setBusy]          = useState(false);
  const [err,           setErr]           = useState<string | null>(null);
  const [scanditResult, setScanditResult] = useState<string | null>(null);
  const [extraOcr,      setExtraOcr]      = useState<string[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // Thumbnail URL for the card preview; zoom uses full-res via ZoomModal.
  const thumbnailUrl = photo.image_url.replace(/\/image$/, '/thumbnail');

  // Lazy-load gate: only do the per-card heavy work (thumbnail fetch, client
  // QR re-scan, server-side OCR) once the card scrolls near the viewport.
  // Rendering 200+ unassigned cards at once otherwise fired 200+ thumbnail
  // fetches + Tesseract OCR jobs on mount, saturating the server and stalling
  // every PATCH /assign behind them.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || inView) return;
    const obs = new IntersectionObserver(
      entries => { if (entries.some(e => e.isIntersecting)) setInView(true); },
      { rootMargin: '400px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000);
    fetch(thumbnailUrl, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('infinium_access_token') ?? ''}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        clearTimeout(timeout);
        objectUrl = URL.createObjectURL(blob);
        setImgSrc(objectUrl);
        import('../services/scanditScanner').then(({ scanImageForQR }) =>
          scanImageForQR(objectUrl!).then(val => { if (val) setScanditResult(val); })
        );
      })
      .catch(statusOrErr => {
        clearTimeout(timeout);
        setImgError(true);
        setImgErrorCode(typeof statusOrErr === 'number' ? statusOrErr : null);
      });
    return () => { clearTimeout(timeout); controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [thumbnailUrl, inView]);

  // Apply code injected when the user clicks a missing-location chip in the sidebar.
  useEffect(() => {
    if (codeInjection?.code) setCode(codeInjection.code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeInjection?.seq]);

  // For photos with no OCR suggestions, request a server-side Tesseract scan.
  // Skip for duplicate photos (noOcr=true) — they already have QR results and
  // firing 40+ concurrent Tesseract jobs would peg the CPU and stall all other
  // requests (including PATCH /assign).
  useEffect(() => {
    if (noOcr) return;
    if (!inView) return;
    if ((photo.ocr_suggestions ?? []).length > 0) return; // already have OCR
    apiClient.post<{ ocr_suggestions: string[] }>(
      `/api/missions/${missionId}/photos/${photo.photo_id}/ocr-scan`, null
    ).then(r => { if (r.ocr_suggestions.length) setExtraOcr(r.ocr_suggestions); })
     .catch(() => {});
  }, [photo.photo_id, missionId, photo.ocr_suggestions, noOcr, inView]);

  const assign = async () => {
    if (!code.trim()) return;
    const normalised  = code.trim().toUpperCase();
    const currentLoc  = (photo.effective_location ?? '').toUpperCase();
    const isTaken     = takenLocations.has(normalised);
    const isSameLoc   = normalised === currentLoc;

    // Warn when assigning to a *different* location that already has a photo.
    // Same-location: user is confirming their own QR result and wants duplicates
    // resolved — skip the dialog and proceed directly with overwrite=true.
    if (isTaken && !isSameLoc) {
      const confirmed = window.confirm(
        `⚠ Location ${normalised} already has a photo assigned.\n\n` +
        `Continuing will overwrite the existing assignment — that photo will move back to Unassigned.\n\n` +
        `Do you want to overwrite?`
      );
      if (!confirmed) return;
    }

    setBusy(true); setErr(null);
    let tid: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, rej) => { tid = setTimeout(() => rej(new Error('Request timed out — check connection and try again.')), 90_000); });
    try {
      await Promise.race([
        apiClient.patch(
          `/api/missions/${missionId}/photos/${photo.photo_id}/assign`,
          { location_code: normalised, overwrite: isTaken },
        ),
        timeout,
      ]);
      clearTimeout(tid!);
      onAssigned(normalised);
    } catch (e) {
      clearTimeout(tid!);
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to assign.');
    } finally {
      setBusy(false);
    }
  };

  // Auto-assign: when the browser's client-side re-scan decodes a barcode that is
  // a valid master location and is NOT already taken by another photo, assign it
  // without waiting for a manual click. Gated to: real master locations only,
  // uncovered slots only, runs once per card. takenLocations updates optimistically
  // so the first reader of a slot wins and duplicate shots are NOT auto-assigned.
  const autoAssignedRef = useRef(false);
  useEffect(() => {
    if (noOcr || autoAssignedRef.current || busy) return;
    if (photo.effective_location || photo.scanned_location) return;
    if (!scanditResult) return;
    const cand = scanditResult.trim().toUpperCase();
    if (!isLikelyLocationCode(cand, knownLocations)) return;
    if (takenLocations.has(cand)) return;
    autoAssignedRef.current = true;
    setCode(cand);
    setBusy(true);
    apiClient
      .patch(`/api/missions/${missionId}/photos/${photo.photo_id}/assign`, { location_code: cand, overwrite: false })
      .then(() => onAssigned(cand))
      .catch(() => { autoAssignedRef.current = false; })
      .finally(() => setBusy(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanditResult, busy, noOcr]);

  // Filename-derived location: if the photo was renamed to its location code
  // before upload (e.g. "10-014-D.jpg" → "10-014-D"), surface it as a suggestion.
  const filenameBase = photo.filename.replace(/\.[^.]+$/, '').toUpperCase();
  const filenameCode = isLikelyLocationCode(filenameBase, knownLocations) ? filenameBase : null;

  const allSuggestions: { value: string; source: 'qr' | 'ocr' | 'filename' | 'phash' }[] = [
    // Filename-derived — highest priority: explicit rename is a strong signal
    ...(filenameCode
      && filenameCode !== (scanditResult ?? '').toUpperCase()
      && filenameCode !== (photo.scanned_location ?? '').toUpperCase()
      ? [{ value: filenameCode, source: 'filename' as const }]
      : []),
    // Client-side Scandit re-scan — filter against known location codes so
    // product/serial barcodes (e.g. "194150078959942252") are never surfaced.
    ...(scanditResult && isLikelyLocationCode(scanditResult, knownLocations)
      ? [{ value: scanditResult, source: 'qr' as const }]
      : []),
    // Server-side QR result — already filtered to QR_TYPES by the backend
    ...(photo.scanned_location
      && photo.scanned_location !== scanditResult
      && isLikelyLocationCode(photo.scanned_location, knownLocations)
      ? [{ value: photo.scanned_location, source: 'qr' as const }]
      : []),
    // Server-stored OCR suggestions (validated against location master server-side)
    ...(photo.ocr_suggestions ?? []).map(s => ({ value: s, source: 'ocr' as const })),
    // On-demand OCR suggestions
    ...extraOcr
      .filter(s => !(photo.ocr_suggestions ?? []).includes(s) && s !== scanditResult && s !== photo.scanned_location)
      .map(s => ({ value: s, source: 'ocr' as const })),
    // Phash-matched sibling (UNCONFIRMED) — location_backfill's duplicate-
    // collapse found this photo visually near-identical to an already-assigned
    // photo, but never auto-assigns it (a dHash alone can't tell a genuine
    // double-shot from two different pallets of similar-looking stock).
    // Weakest signal of the bunch — always verify the photo before confirming.
    ...(photo.phash_suggested_location
      ? [{ value: photo.phash_suggested_location, source: 'phash' as const }]
      : []),
  ];

  // Label-based "nearby": the OTHER rack labels actually decoded in THIS photo
  // (beam labels in the frame) — far more reliable than numeric ±2 guesses,
  // since the pallet QR can point at a slot the pallet was moved from. Exclude
  // the primary decode and anything already offered as a detected suggestion.
  // Fall back to numeric adjacents only when no extra labels were decoded.
  const _shownValues = new Set([
    ...allSuggestions.map(s => s.value.toUpperCase()),
    (photo.scanned_location ?? '').toUpperCase(),
  ]);
  const labelNearby = (photo.decoded_locations ?? [])
    .filter(l => l && !_shownValues.has(l.toUpperCase()));
  const nearbyIsLabelBased = labelNearby.length > 0;
  const nearbyLocations = nearbyIsLabelBased ? labelNearby : (adjacentLocations ?? []);

  return (
    <>
    <div ref={cardRef} className={`flex rounded-xl border bg-white overflow-hidden shadow-sm transition-shadow ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
      {/* Card content (left). The Select button is a full-height column on the right edge. */}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Header bar */}
      <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isSelected ? 'bg-blue-50' : 'bg-amber-50'}`}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="font-mono text-sm font-semibold text-gray-800 truncate max-w-xs">
            {photo.filename}
          </span>
          {photo.file_size_bytes && (
            <span className="text-xs text-muted-foreground">
              {(photo.file_size_bytes / 1_048_576).toFixed(1)} MB
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Defer ("Do not assign") lives in the HEADER, spatially opposite the
              Assign action — it used to sit full-width directly beneath the
              Assign button, where an overshoot click silently removed the photo
              from the queue (Fitts's-law hazard). Compact target + border so it
              reads as a deliberate control, never on the type→Assign path. */}
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await apiClient.patch(
                  `/api/missions/${missionId}/photos/${photo.photo_id}/assign`,
                  { location_code: '__SKIPPED__' },
                );
                onAssigned(undefined);
              } catch {
                // non-fatal — photo stays unassigned
              } finally {
                setBusy(false);
              }
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground border border-transparent rounded-md px-2 py-1 hover:text-destructive hover:border-destructive/40 transition-colors"
            title="Do not assign this photo — send it to the review (deferred) pool"
          >
            <Ban className="h-3.5 w-3.5" />
            Do not assign
          </button>
          <span className="text-xs text-muted-foreground">{index} of {total}</span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-0">
        {/* Large photo */}
        <div className="lg:w-2/3 bg-gray-100 flex items-center justify-center min-h-[400px] relative">
          {imgError ? (
            <div className="flex flex-col items-center gap-2 text-muted-foreground/50 p-8 text-center">
              <ImageOff className="h-12 w-12" />
              <span className="text-sm">Preview unavailable</span>
              <span className="text-xs">
                {imgErrorCode === 404
                  ? 'Image file not found — may have been moved after processing'
                  : imgErrorCode === 401 || imgErrorCode === 403
                  ? 'Auth error — try refreshing the page'
                  : imgErrorCode
                  ? `Server error (${imgErrorCode})`
                  : 'Network error — check your connection'}
              </span>
            </div>
          ) : imgSrc ? (
            <img
              src={imgSrc}
              alt={photo.filename}
              className="w-full h-full object-contain max-h-[600px] cursor-zoom-in"
              onClick={() => setZoomed(true)}
              onMouseEnter={() => prefetchViewer(photo.image_url)}
              title="Click to enlarge"
            />
          ) : (
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground/40" />
          )}
          {/* Status badge */}
          <div className="absolute top-3 left-3">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide
              ${photo.status === 'DONE'    ? 'bg-green-500 text-white' :
                photo.status === 'FAILED'  ? 'bg-red-500 text-white' :
                photo.status === 'PROCESSING' ? 'bg-blue-500 text-white' :
                'bg-gray-400 text-white'}`}>
              {photo.status}
            </span>
          </div>
        </div>

        {/* Assignment panel */}
        <div className="lg:w-1/3 p-5 flex flex-col gap-4 border-l">
          {photo.likely_duplicate && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Likely duplicate.</span>{' '}
                {(photo.recycle_count ?? 0) > 0
                  ? `Deferred ${photo.recycle_count} time${photo.recycle_count === 1 ? '' : 's'} already — `
                  : 'Near-identical to another photo — '}
                consider <span className="font-semibold">“Do not assign”</span> in the header above.
              </p>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-sm mb-1">Assign Location</h3>
            <p className="text-xs text-muted-foreground">
              Type or select a location code from the suggestions below, then click Assign.
            </p>
          </div>

          {/* Detected suggestions (QR / OCR) */}
          {allSuggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Detected suggestions
              </p>
              <div className="flex flex-wrap gap-2">
                {allSuggestions.map(({ value, source }) => (
                  <button
                    key={`${source}-${value}`}
                    type="button"
                    onClick={() => setCode(value)}
                    disabled={busy}
                    className={`
                      text-sm font-mono font-semibold px-3 py-1.5 rounded-lg border-2 transition-all
                      ${code === value
                        ? source === 'qr'      ? 'bg-blue-600   text-white border-blue-600'
                        : source === 'filename' ? 'bg-amber-500  text-white border-amber-500'
                        : source === 'phash'    ? 'bg-purple-600 text-white border-purple-600'
                        :                        'bg-green-600  text-white border-green-600'
                        : source === 'qr'      ? 'bg-blue-50   text-blue-700  border-blue-300  hover:bg-blue-100'
                        : source === 'filename' ? 'bg-amber-50  text-amber-700 border-amber-300 hover:bg-amber-100'
                        : source === 'phash'    ? 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100'
                        :                        'bg-green-50  text-green-700  border-green-300 hover:bg-green-100'
                      }
                    `}
                    title={
                      source === 'qr'       ? 'Decoded from QR code' :
                      source === 'filename' ? 'Derived from filename' :
                      source === 'phash'    ? 'Unconfirmed — visually similar to another assigned photo. Verify before confirming.' :
                                             'Detected by OCR'
                    }
                  >
                    {source === 'qr' ? '◈ ' : source === 'filename' ? '⌗ ' : source === 'phash' ? '≈ ' : '✓ '}{value}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Nearby locations — label-based (rack labels decoded in this photo)
              when available, else numeric adjacents as a typing shortcut. */}
          {nearbyLocations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {nearbyIsLabelBased ? 'Labels in this photo' : 'Nearby locations'}
              </p>
              <div className="flex flex-wrap gap-2">
                {nearbyLocations.map(loc => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setCode(loc)}
                    disabled={busy}
                    className={`
                      text-sm font-mono font-semibold px-3 py-1.5 rounded-lg border-2 transition-all
                      ${code === loc
                        ? nearbyIsLabelBased ? 'bg-blue-600 text-white border-blue-600'
                                             : 'bg-gray-700 text-white border-gray-700'
                        : nearbyIsLabelBased ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                                             : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                      }
                    `}
                    title={nearbyIsLabelBased
                      ? 'Rack label decoded in this photo — click to select'
                      : 'Adjacent location — click to select'}
                  >
                    {nearbyIsLabelBased ? '◈ ' : ''}{loc}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Manual input */}
          <div className="space-y-2 mt-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Location code
            </p>
            <div className="flex gap-2">
              <input
                className="form-input flex-1 text-base py-2 px-3 font-mono uppercase rounded-lg border"
                placeholder="e.g. 10-019-F"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && assign()}
                disabled={busy}
                autoComplete="off"
              />
              <Button
                className="px-4"
                disabled={busy || !code.trim()}
                onClick={assign}
              >
                {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Assign'}
              </Button>
            </div>
            {err && <p className="text-xs text-destructive">{err}</p>}
          </div>

          {/* Scan info */}
          <div className="pt-2 border-t space-y-1">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{scanSourceLabel(photo.scan_source)}:</span>{' '}
              {photo.scanned_location
                ? <span className="text-green-700">{photo.scanned_location} ({Math.round((photo.scan_confidence ?? 0) * 100)}%)</span>
                : <span className="text-amber-600">Not decoded</span>}
            </p>
            {photo.ocr_suggestions && photo.ocr_suggestions.length > 0 && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">OCR:</span>{' '}
                <span className="text-green-700">{photo.ocr_suggestions.join(', ')}</span>
              </p>
            )}
            {(photo.recycle_count ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">No. of Recycles:</span>{' '}
                <span className={photo.likely_duplicate ? 'text-amber-700 font-semibold' : ''}>
                  {photo.recycle_count}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
      </div>
      {/* Full-height Select button on the right edge — large hit target, no extra card height */}
      <button
        type="button"
        onClick={() => onSelect?.()}
        aria-pressed={isSelected}
        className={`shrink-0 w-14 flex items-center justify-center border-l transition-colors ${
          isSelected
            ? 'bg-blue-500 text-white'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'
        }`}
        title="Select this card, then click a missing location in the sidebar to fill it"
      >
        <span style={{ writingMode: 'vertical-rl' }} className="rotate-180 text-sm font-semibold tracking-wide whitespace-nowrap">
          {isSelected ? '✓ Selected' : 'Select'}
        </span>
      </button>
    </div>
    {zoomed && (
      <ZoomModal url={photo.image_url} filename={photo.filename} onClose={() => setZoomed(false)} />
    )}
    </>
  );
}


// ── Section headers ───────────────────────────────────────────────────────────

function SectionHeader({
  icon, title, count, variant,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  variant: 'success' | 'warning' | 'destructive' | 'muted';
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h2 className="font-semibold text-base">{title}</h2>
      <Badge variant={variant} className="ml-1">{count}</Badge>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ExceptionTriage() {
  const { missionId }       = useParams<{ missionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate             = useNavigate();
  useAuth(); // establishes auth context; specific user fields not needed here

  const aisleParam = (searchParams.get('aisle') ?? '').toUpperCase();

  const [triage,          setTriage]          = useState<TriageResponse | null>(null);
  const [matchedOpen,     setMatchedOpen]     = useState(false);
  const [unassignedLimit, setUnassignedLimit] = useState(20);
  const [loading,         setLoading]         = useState(!!aisleParam); // only true if aisle known upfront
  const [error,           setError]           = useState<string | null>(null);
  const [selectedAisle,   setSelectedAisle]   = useState(aisleParam);
  const [requeuing,       setRequeuing]       = useState(false);
  const [reprocessing,    setReprocessing]    = useState(false);
  const [missionAisles,   setMissionAisles]   = useState<string[]>([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [codeInjection,   setCodeInjection]   = useState<{ code: string; seq: number } | null>(null);

  // Fetch allocated aisles for this mission once (for the aisle picker).
  useEffect(() => {
    if (!missionId) return;
    apiClient.get<{ allocated_aisles: string[] }>(`/api/missions/${missionId}`)
      .then(d => setMissionAisles((d.allocated_aisles ?? []).sort()))
      .catch(() => {}); // non-fatal
  }, [missionId]);

  // Known mis-pasted QR labels for this warehouse (confirmed registry) —
  // surfaced BEFORE triage so pilots recognise these decodes instead of
  // re-investigating them; the pipeline auto-corrects them on ingest.
  const [labelRegistry, setLabelRegistry] = useState<
    { location_code: string; actual_location: string; note: string }[]
  >([]);
  useEffect(() => {
    if (!missionId) return;
    apiClient.get<{ location_code: string; actual_location: string; note: string }[]>(
      `/api/qr-worklist/missions/${missionId}/label-registry`,
    )
      .then(setLabelRegistry)
      .catch(() => {}); // non-fatal
  }, [missionId]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Autonomous missing-list tracking: locations that LEFT the missing list
  // between refreshes were just covered by newly ingested photos — surface
  // them green at the top of "Still Needed" so the pilot sees progress live.
  const prevMissingRef = useRef<{ aisle: string; set: Set<string> } | null>(null);
  const [justCovered, setJustCovered] = useState<Map<string, number>>(new Map());

  // Copy the missing-locations list as a WhatsApp-ready message the manager
  // pastes straight into the pilot chat.
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);
  const copyMissing = useCallback(async (locs: string[], aisle: string) => {
    if (locs.length === 0) return;
    const sorted = [...locs].sort();
    const msg =
      `📍 Aisle ${aisle} — ${sorted.length} location${sorted.length === 1 ? '' : 's'} still need a photo (please retake):\n\n`
      + sorted.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(msg);
      } else {
        // Fallback for non-secure contexts / older browsers.
        const ta = document.createElement('textarea');
        ta.value = msg; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed — select the list manually.');
    }
  }, []);

  const load = useCallback(async (aisle?: string, silent = false) => {
    const a = (aisle ?? selectedAisle).trim().toUpperCase();
    if (!missionId || !a) return;
    if (!silent) setLoading(true);
    try {
      const data = await apiClient.get<TriageResponse>(
        `/api/missions/${missionId}/triage?aisle=${encodeURIComponent(a)}`,
      );
      setTriage(data);
      // Diff the missing list against the previous refresh (same aisle only).
      const missingNow = new Set(data.missing.map(m => m.toUpperCase()));
      const prev = prevMissingRef.current;
      const now = Date.now();
      if (prev && prev.aisle === a) {
        setJustCovered(jc => {
          const next = new Map(jc);
          prev.set.forEach(loc => { if (!missingNow.has(loc)) next.set(loc, now); });
          for (const [k, t] of next) {
            if (now - t > 120_000 || missingNow.has(k)) next.delete(k);   // prune / re-missing
          }
          return next;
        });
      } else if (!prev || prev.aisle !== a) {
        setJustCovered(new Map());
      }
      prevMissingRef.current = { aisle: a, set: missingNow };
      if (!silent) setUnassignedLimit(20);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load triage.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [missionId, selectedAisle]);

  // Debounced authoritative sync: re-fetching the full triage + re-rendering
  // every card on EACH assign made rapid assigning lag. Instead, after a burst
  // of assigns, sync once when the user pauses (~1.5s). The optimistic per-assign
  // state update already keeps the visible list correct in the meantime.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSilentReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { load(undefined, true); }, 1500);
  }, [load]);
  useEffect(() => () => { if (reloadTimer.current) clearTimeout(reloadTimer.current); }, []);

  // Initial load
  useEffect(() => {
    if (aisleParam) load(aisleParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aisleParam]);

  // Autonomous refresh loop — ALWAYS on: fast (5s) while photos are processing,
  // slow (15s) when idle so a fresh upload batch is noticed and the missing
  // list updates with no user action.
  useEffect(() => {
    const fast = !!triage && triage.processing_count > 0;
    pollRef.current = setInterval(() => load(undefined, true), fast ? 5_000 : 15_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [triage?.processing_count, load]);

  // When NEW photos arrive (uploaded count changed), run the backend coverage
  // pass — it persists autonomous flight-sequence inferences (gap-exact guard)
  // so sandwiched unassigned photos resolve without triage — then re-sync.
  const lastUploadedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!triage || !missionId) return;
    const uploaded = triage.photos_uploaded;
    if (lastUploadedRef.current !== null && uploaded !== lastUploadedRef.current) {
      apiClient.get<{ sequence_inferred_now: number }>(`/api/missions/${missionId}/coverage`)
        .then(c => { if (c.sequence_inferred_now > 0) load(undefined, true); })
        .catch(() => {});   // non-fatal — the poll keeps the list current anyway
    }
    lastUploadedRef.current = uploaded;
  }, [triage?.photos_uploaded, missionId, load]);

  const handleAisleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAisle.trim()) return;
    setSearchParams({ aisle: selectedAisle.trim().toUpperCase() });
    load(selectedAisle);
  };

  const handleRequeue = async () => {
    if (!missionId) return;
    setRequeuing(true);
    try {
      await apiClient.post(`/api/missions/${missionId}/requeue`, null);
      await load(undefined, true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Requeue failed.');
    } finally {
      setRequeuing(false);
    }
  };

  const handleReprocess = async () => {
    if (!missionId) return;
    if (!window.confirm(
      'Re-decode EVERY photo in this mission through the current detection engine?\n\n'
      + 'Use this after changing the active config in the Detection Lab. Photos you '
      + 'have manually assigned are preserved; matched and duplicate results are refreshed.'
    )) return;
    setReprocessing(true);
    try {
      await apiClient.post(`/api/missions/${missionId}/reprocess`, null);
      await load(undefined, true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reprocess failed.');
    } finally {
      setReprocessing(false);
    }
  };

  const total  = triage ? triage.assigned_location_count : 0;
  const done   = triage ? triage.matched.length : 0;

  // Set of location codes that already have at least one photo assigned.
  // Used by PhotoCard / UnassignedTriageRow to warn before overwriting.
  const takenLocations = useMemo<Set<string>>(() => {
    if (!triage) return new Set();
    const s = new Set<string>();
    triage.matched.forEach(m => s.add(m.location_code));
    triage.duplicates.forEach(d => s.add(d.location_code));
    return s;
  }, [triage]);

  // All valid location codes for this aisle — used to filter out product
  // barcodes from the Scandit client-side re-scan suggestions.
  const knownLocations = useMemo<Set<string>>(() => {
    if (!triage) return new Set();
    const s = new Set<string>();
    triage.matched.forEach(m => s.add(m.location_code.toUpperCase()));
    triage.missing.forEach(c => s.add(c.toUpperCase()));
    triage.duplicates.forEach(d => s.add(d.location_code.toUpperCase()));
    return s;
  }, [triage]);
  const pct    = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-10 border-b bg-white px-3 sm:px-6 py-3 flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-sm"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base">Exception Triage</h1>
          {triage && (
            <p className="text-xs text-muted-foreground">
              Aisle {triage.aisle} · Mission {missionId?.slice(0, 8)}…
            </p>
          )}
        </div>

        {/* Aisle switcher */}
        <form onSubmit={handleAisleSubmit} className="flex gap-2 items-center">
          <input
            className="form-input h-8 text-sm py-1 px-2 font-mono uppercase w-24"
            placeholder="Aisle…"
            value={selectedAisle}
            onChange={e => setSelectedAisle(e.target.value.toUpperCase())}
          />
          <Button type="submit" size="sm" className="h-8 text-xs" disabled={loading || !selectedAisle.trim()}>
            {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'Load'}
          </Button>
        </form>

        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2 text-xs"
          onClick={() => load()}
          disabled={loading}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">

        {/* ── Back button ─────────────────────────────────────────────────── */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 px-2 text-sm -ml-2"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
        </div>

        {/* ── Error ───────────────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* ── Known QR label issues (confirmed registry) ─────────────────── */}
        {labelRegistry.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm space-y-1.5">
            <p className="font-semibold text-amber-900">
              ⚠ Known QR label issues in this warehouse — decodes auto-corrected
            </p>
            <ul className="space-y-1">
              {labelRegistry.map(r => (
                <li key={r.location_code} className="text-amber-900/90">
                  <span className="font-mono font-semibold">{r.location_code}</span>
                  {' '}label is physically at{' '}
                  <span className="font-mono font-semibold">{r.actual_location}</span>
                  {' '}— photos decoding it are allocated to {r.actual_location} automatically.
                  {r.note ? <span className="text-amber-800/80"> {r.note}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── No aisle selected — aisle picker ─────────────────────────── */}
        {!aisleParam && (
          <div className="rounded-xl border bg-white p-8 space-y-6">
            <div className="text-center space-y-1">
              <h2 className="text-lg font-semibold">Select an aisle to triage</h2>
              <p className="text-sm text-muted-foreground">
                Choose from the mission's allocated aisles, or type one manually.
              </p>
            </div>

            {/* Allocated aisle chips */}
            {missionAisles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">
                  Allocated aisles
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {missionAisles.map(a => (
                    <button
                      key={a}
                      type="button"
                      className="font-mono font-semibold px-4 py-2 rounded-lg border-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors text-sm"
                      onClick={() => {
                        setSelectedAisle(a);
                        setSearchParams({ aisle: a });
                      }}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Manual entry */}
            <form onSubmit={handleAisleSubmit} className="flex gap-2 max-w-xs mx-auto">
              <input
                className="form-input flex-1 text-sm py-2 px-3 font-mono uppercase rounded-lg border"
                placeholder="Type aisle code…"
                value={selectedAisle}
                onChange={e => setSelectedAisle(e.target.value.toUpperCase())}
                autoFocus
              />
              <Button type="submit" disabled={!selectedAisle.trim()}>
                Load
              </Button>
            </form>
          </div>
        )}

        {triage && (
          <>
            {/* ── Stats bar ─────────────────────────────────────────────── */}
            <div className="rounded-xl border bg-white p-4 space-y-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Assigned</span>
                  <p className="font-bold text-lg mt-0.5">{triage.assigned_location_count}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Uploaded</span>
                  <p className="font-bold text-lg mt-0.5">{triage.photos_uploaded}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Matched</span>
                  <p className="font-bold text-lg mt-0.5 text-green-700">{triage.matched.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Duplicates</span>
                  <p className="font-bold text-lg mt-0.5 text-amber-600">{triage.duplicates.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Missing</span>
                  <p className="font-bold text-lg mt-0.5 text-red-600">{triage.missing.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Unassigned</span>
                  <p className="font-bold text-lg mt-0.5 text-amber-600">{triage.unassigned_photos.length}</p>
                </div>
                {triage.processing_count > 0 && (
                  <div>
                    <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Processing</span>
                    <p className="font-bold text-lg mt-0.5 text-blue-600 flex items-center gap-1">
                      <RefreshCw className="h-4 w-4 animate-spin" /> {triage.processing_count}
                    </p>
                  </div>
                )}
                {(triage.unassigned_photos.length > 0
                  || triage.duplicates.length > 0
                  || triage.matched.length > 0
                  || triage.processing_count > 0) && (
                  <div className="flex items-end gap-2">
                    {(triage.unassigned_photos.length > 0 || triage.processing_count > 0) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={handleRequeue}
                        disabled={requeuing || reprocessing || triage.processing_count > 0}
                      >
                        {requeuing && <RefreshCw className="h-3 w-3 animate-spin mr-1" />}
                        Requeue unassigned
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={handleReprocess}
                      disabled={requeuing || reprocessing || triage.processing_count > 0}
                      title="Re-decode every photo in this mission through the current engine. Disabled while a reprocess is running — wait for it to finish before starting another."
                    >
                      {(reprocessing || triage.processing_count > 0) && <RefreshCw className="h-3 w-3 animate-spin mr-1" />}
                      {triage.processing_count > 0 ? `Reprocessing… (${triage.processing_count})` : 'Reprocess all'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Coverage — {pct}% matched</span>
                  <span>{done} / {total}</span>
                </div>
                <Progress
                  value={pct}
                  variant={pct === 100 ? 'success' : triage.missing.length > 0 ? 'warning' : 'default'}
                />
              </div>
            </div>

            {/* ── Two-column layout: missing sidebar + main ─────────────── */}
            <div className="flex flex-col sm:flex-row gap-6 items-start">

              {/* Missing sidebar */}
              {(triage.missing.length > 0 || justCovered.size > 0) && (
                <aside className="w-full sm:w-52 shrink-0 sm:sticky sm:top-[68px]">
                  <div className="rounded-xl border bg-white p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <h2 className="font-semibold text-sm">Still Needed</h2>
                      {/* Live auto-update indicator — the list refreshes itself
                          as new photos are ingested (no button needed). */}
                      <span
                        className="relative flex h-2 w-2"
                        title="Live — auto-updating as photos are ingested"
                      >
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                      <Badge variant="destructive" className="ml-auto">{triage.missing.length}</Badge>
                    </div>

                    {/* Copy for WhatsApp — paste the retake list straight into
                        the pilot chat. */}
                    {triage.missing.length > 0 && (
                      <button
                        type="button"
                        onClick={() => copyMissing(triage.missing, selectedAisle.trim().toUpperCase())}
                        className={`w-full flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 mb-3 text-xs font-medium border transition-colors
                          ${copied
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        title="Copy the missing-location list for WhatsApp"
                      >
                        {copied
                          ? (<><Check className="h-3.5 w-3.5" /> Copied {triage.missing.length} locations</>)
                          : (<><Copy className="h-3.5 w-3.5" /> Copy List</>)}
                      </button>
                    )}
                    {/* Locations covered by the LATEST ingest — highlighted so the
                        pilot sees live progress against the missing list. */}
                    {justCovered.size > 0 && (
                      <div className="mb-3">
                        <p className="text-[11px] font-semibold text-emerald-700 mb-1">
                          ✓ Just covered ({justCovered.size})
                        </p>
                        <div className="flex flex-col gap-1 max-h-[20vh] overflow-y-auto">
                          {[...justCovered.entries()]
                            .sort((x, y) => y[1] - x[1])
                            .map(([loc]) => (
                              <div
                                key={loc}
                                className="w-full rounded-md px-2 py-1 text-xs font-mono font-semibold
                                           bg-emerald-50 border border-emerald-300 text-emerald-800"
                              >
                                ✓ {loc}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {selectedPhotoId ? (
                      <p className="text-xs text-blue-600 font-medium mb-2">
                        Click a location to fill the selected card.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mb-2">
                        Click a photo card header to select it, then click a location here to fill it.
                      </p>
                    )}
                    <div className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto">
                      {triage.missing.map(loc => (
                        <button
                          key={loc}
                          type="button"
                          disabled={!selectedPhotoId}
                          onClick={() => setCodeInjection(prev => ({ code: loc, seq: (prev?.seq ?? 0) + 1 }))}
                          className={`w-full text-left rounded-md px-2 py-1 text-xs font-mono font-semibold transition-colors
                            ${selectedPhotoId
                              ? 'bg-red-50 border border-red-200 text-red-800 hover:bg-blue-600 hover:text-white hover:border-blue-600 cursor-pointer'
                              : 'bg-red-50 border border-red-200 text-red-800 opacity-50 cursor-not-allowed'}`}
                          title={selectedPhotoId ? `Fill selected card with ${loc}` : 'Select a photo card first'}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>
                </aside>
              )}

              {/* Main content */}
              <div className="flex-1 min-w-0 space-y-8">

                {/* ── Duplicates — large triage view ─────────────────── */}
                {triage.duplicates.length > 0 && (
                  <section>
                    <SectionHeader
                      icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
                      title="Duplicate Photos"
                      count={triage.duplicates.length}
                      variant="warning"
                    />
                    <p className="text-sm text-muted-foreground mb-4">
                      Two or more photos physically decoded the same location. Keep the best
                      shot and mark the extras <span className="font-medium">“Do not assign”</span> —
                      or reassign a photo if it’s actually a different slot.
                    </p>
                    <div className="space-y-8">
                      {triage.duplicates.map((group: TriageDuplicateGroup) => (
                        <div key={group.location_code}>
                          <div className="flex items-center gap-2 mb-3">
                            <code className="text-sm font-bold font-mono bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                              {group.location_code}
                            </code>
                            <span className="text-xs text-amber-700 font-medium">
                              {group.photos.length} photos of this location — keep the best, mark the rest “Do not assign”
                            </span>
                          </div>
                          <div className="flex flex-col gap-4">
                            {group.photos.map((photo, idx) => (
                              <div key={photo.photo_id}>
                                {/* Advisory ranking from the backfill: which shot
                                    is the best of this slot. Null when the group
                                    was never ranked — then no hint is shown. */}
                                {photo.is_representative === true && (
                                  <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium text-green-700">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Suggested best shot — keep this one
                                  </div>
                                )}
                                {photo.is_representative === false && (
                                  <div className="mb-1.5 text-xs text-muted-foreground">
                                    Extra shot — mark “Do not assign” if it’s the same pallet
                                  </div>
                                )}
                              <UnassignedTriageRow
                                photo={photo}
                                index={idx + 1}
                                total={group.photos.length}
                                missionId={missionId!}
                                takenLocations={takenLocations}
                                knownLocations={knownLocations}
                                noOcr={true}
                                isSelected={selectedPhotoId === photo.photo_id}
                                onSelect={() => setSelectedPhotoId(photo.photo_id)}
                                codeInjection={selectedPhotoId === photo.photo_id ? codeInjection : null}
                                onAssigned={async (assignedCode?: string) => {
                                  // Optimistic: collapse the duplicate group immediately so
                                  // the UI feels instant. The silent background reload below
                                  // then resolves the final authoritative state.
                                  setTriage(prev => prev ? {
                                    ...prev,
                                    duplicates: prev.duplicates.filter(
                                      d => d.location_code !== group.location_code,
                                    ),
                                    missing: assignedCode
                                      ? prev.missing.filter(m => m !== assignedCode)
                                      : prev.missing,
                                  } : prev);

                                  // When a 2-photo group has one photo reassigned to a
                                  // DIFFERENT location, auto-assign the sibling to the
                                  // group's original code so step8 copy runs without
                                  // requiring an extra click.
                                  if (
                                    group.photos.length === 2 &&
                                    assignedCode &&
                                    assignedCode !== group.location_code
                                  ) {
                                    const sibling = group.photos.find(p => p.photo_id !== photo.photo_id);
                                    if (sibling && !sibling.manual_location) {
                                      try {
                                        await apiClient.patch(
                                          `/api/missions/${missionId}/photos/${sibling.photo_id}/assign`,
                                          { location_code: group.location_code },
                                        );
                                      } catch {
                                        // non-fatal — sibling can still be assigned manually
                                      }
                                    }
                                  }
                                  scheduleSilentReload(); // debounced background sync
                                }}
                                adjacentLocations={getAdjacentLocations(group.location_code)}
                              />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Unassigned photos — large triage view ──────────── */}
                {triage.unassigned_photos.length > 0 && (
                  <section>
                    <SectionHeader
                      icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
                      title="Unassigned Photos"
                      count={triage.unassigned_photos.length}
                      variant="warning"
                    />
                    <p className="text-sm text-muted-foreground mb-4">
                      These photos could not be matched automatically. Inspect each photo,
                      use any detected location chips to pre-fill, then click <strong>Assign</strong>.
                    </p>
                    <div className="flex flex-col gap-6">
                      {triage.unassigned_photos.slice(0, unassignedLimit).map((photo, idx) => (
                        <UnassignedTriageRow
                          key={photo.photo_id}
                          photo={photo}
                          index={idx + 1}
                          total={triage.unassigned_photos.length}
                          missionId={missionId!}
                          takenLocations={takenLocations}
                          knownLocations={knownLocations}
                          isSelected={selectedPhotoId === photo.photo_id}
                          onSelect={() => setSelectedPhotoId(photo.photo_id)}
                          codeInjection={selectedPhotoId === photo.photo_id ? codeInjection : null}
                          onAssigned={(assignedCode) => {
                            // Optimistic update: remove photo from unassigned and its
                            // code from the missing list immediately so the UI feels
                            // instant. Silent reload syncs authoritative state after.
                            if (assignedCode) {
                              setTriage(prev => prev ? {
                                ...prev,
                                unassigned_photos: prev.unassigned_photos.filter(
                                  p => p.photo_id !== photo.photo_id,
                                ),
                                missing: prev.missing.filter(m => m !== assignedCode),
                              } : prev);
                            }
                            scheduleSilentReload(); // debounced background sync
                          }}
                        />
                      ))}
                      {triage.unassigned_photos.length > unassignedLimit && (
                        <button
                          type="button"
                          onClick={() => setUnassignedLimit(c => c + 25)}
                          className="btn btn--ghost btn--sm self-start"
                        >
                          Show 25 more · {triage.unassigned_photos.length - unassignedLimit} more below
                        </button>
                      )}
                    </div>
                  </section>
                )}

                {/* ── Deferred (Do not assign) ───────────────────────────── */}
                {(triage.deferred_photos ?? []).length > 0 && (
                  <section>
                    <SectionHeader
                      icon={<AlertTriangle className="h-4 w-4 text-gray-400" />}
                      title="Deferred — Do Not Assign"
                      count={(triage.deferred_photos ?? []).length}
                      variant="muted"
                    />
                    <p className="text-sm text-muted-foreground mb-4">
                      These photos were skipped. Restore them to Unassigned to assign later,
                      or leave them here to be revisited from the flight page.
                    </p>
                    <div className="flex flex-col gap-3">
                      {(triage.deferred_photos ?? []).map((photo: TriagePhoto) => (
                        <div
                          key={photo.photo_id}
                          className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border bg-gray-50"
                        >
                          <span className="text-sm font-mono text-gray-600 truncate">
                            {photo.filename}
                          </span>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm shrink-0"
                            onClick={async () => {
                              try {
                                await apiClient.patch(
                                  `/api/missions/${missionId}/photos/${photo.photo_id}/assign`,
                                  { location_code: null },
                                );
                                load();
                              } catch { /* non-fatal */ }
                            }}
                          >
                            Restore to Unassigned
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Matched ────────────────────────────────────────────── */}
                {triage.matched.length > 0 && (
                  <section>
                    <SectionHeader
                      icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
                      title="Matched"
                      count={triage.matched.length}
                      variant="success"
                    />
                    <details onToggle={e => setMatchedOpen((e.currentTarget as HTMLDetailsElement).open)}>
                      <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground mb-3">
                        Show {triage.matched.length} clean match{triage.matched.length !== 1 ? 'es' : ''}
                      </summary>
                      {matchedOpen && (
                      <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {triage.matched.map((item: TriageMatchedItem) => (
                          <PhotoCard
                            key={item.photo.photo_id}
                            photo={item.photo}
                            missionId={missionId!}
                            onAssigned={() => load()}
                            showAssignInput={false}
                            dim={false}
                            takenLocations={takenLocations}
                          />
                        ))}
                      </div>
                      )}
                    </details>
                  </section>
                )}

                {/* ── All clear ──────────────────────────────────────────── */}
                {triage.missing.length === 0 &&
                  triage.duplicates.length === 0 &&
                  triage.unassigned_photos.length === 0 &&
                  triage.matched.length > 0 && (
                  <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
                    <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-green-600" />
                    <p className="font-semibold text-green-800">
                      Aisle {triage.aisle} — All {triage.matched.length} locations matched!
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      This aisle is ready for AI inference.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
